import _ from 'underscore';

import * as AssemblyPrimers from 'assembly/AssemblyPrimers';
import {Collection as Annotations} from 'biojs/Annotation';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {Model as BioJSSequencePrimer, SequencePrimerAttributes} from 'biojs/SequencePrimer';
import {revcomp} from 'biojs/Util';
import {Sequence as ComponentSequence} from 'components/Sequence';
import {PrimerPreferences} from 'constants/PrimerConstants';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import * as navigation from 'Navigation';
import notifications from 'notifications';
import preferences from 'Preferences';
import * as TaskRunner from 'util/TaskRunner';

export interface EditPrimerTabStateDefaults extends navigation.TabStateDefaults {
  primer: BioJSSequencePrimer;
  primer2: BioJSSequencePrimer | null;
  saving: [boolean, boolean];
  checking: boolean;
}

export interface Secondary {
  monomer: {dg: number; svg: string};
  homodimer: {dg: number; svg: string};
  monomer2?: {dg: number; svg: string};
  homodimer2?: {dg: number; svg: string};
  heterodimer?: {dg: number; svg: string};
}
export interface EditPrimerTabStateAttributes extends EditPrimerTabStateDefaults {
  sequence: ComponentSequence;
  analyzeSecondary?: boolean;
  temperature?: number;
  secondary?: Secondary;
}

interface EditPrimerTabState {
  previous<K extends keyof EditPrimerTabStateAttributes>(key: K): EditPrimerTabStateAttributes[K];
  get<K extends keyof EditPrimerTabStateAttributes>(key: K): EditPrimerTabStateAttributes[K];
  set(obj: Partial<EditPrimerTabStateAttributes>): EditPrimerTabState;
  set<K extends keyof EditPrimerTabStateAttributes>(
    key: K,
    value: EditPrimerTabStateAttributes[K]
  ): EditPrimerTabState;
  // these extra attributes are tacked on to the model directly
  _sequence: BioJSSequence;
  originalPrimer?: SequencePrimerAttributes;
  originalPrimer2?: SequencePrimerAttributes;
}
class EditPrimerTabState extends navigation.TabState {
  initialize(): void {
    this._sequence = this.get('sequence').get('_sequence');

    this.get('primer').set('sequenceID', this.get('sequence').id);
    this.listenTo(this._sequence, 'change:annotations', (seq: BioJSSequence, annotations: Annotations) => {
      if (this.get('sequence').get('currentVersion')) {
        this.addValidPrimers();
      }
    });

    this.listenToPrimer('primer', 'originalPrimer');
    this.listenToPrimer('primer2', 'originalPrimer2');

    this.on('change:checking', (model: EditPrimerTabState, value: boolean) => {
      if (value) {
        this.checkSecondary();
      }
    });
    const val = preferences.get(PrimerPreferences.SECONDARY_TEMP);
    if (val != null && !Number.isNaN(parseInt(val, 10))) {
      this.set({temperature: parseInt(val, 10)});
    }
    this.on('change:temperature', (model: EditPrimerTabState, value: number | null) => {
      if (value != null) {
        preferences.set(PrimerPreferences.SECONDARY_TEMP, value);
      } else {
        preferences.unset(PrimerPreferences.SECONDARY_TEMP);
      }
    });
    this.listenTo(
      preferences,
      `change:${PrimerPreferences.SECONDARY_TEMP}`,
      (model: EditPrimerTabState, val: number | string | null) => {
        const temperature = parseInt(val?.toString() ?? '', 10);
        if (!Number.isNaN(temperature)) {
          this.set({temperature});
        }
      }
    );
  }

  listenToPrimer(
    primerAttr: 'primer' | 'primer2',
    originalField: 'originalPrimer' | 'originalPrimer2'
  ): void {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const setAndListenToPrimer = () => {
      // Stop listening to old primer
      const oldPrimer = this.previous(primerAttr);
      if (oldPrimer != null) {
        this.stopListening(oldPrimer);
      }

      // Listen to new primer
      const primer = this.get(primerAttr);
      if (primer != null) {
        this.listenTo(this.get(primerAttr), 'remove', () => {
          this.close();
        });
      }

      // Store new primer as original primer
      this[originalField] = _.clone(primer?.attributes);
    };

    this.on(`change:${primerAttr}`, setAndListenToPrimer);
    setAndListenToPrimer();
  }

  getAssemblyEnzyme(): Enzyme | undefined {
    return this._sequence.assembly()?.enzyme();
  }

  checkSecondary(): JQueryPromise<void> {
    const formData = new FormData();
    let reverse = false;
    const primer = this.get('primer');
    const primer2 = this.get('primer2');
    if (primer.bases().length !== 0) {
      formData.append('primer', primer.bases());
      formData.append('nucleotideType', primer.nucleotideType());
      if (primer2 != null && primer2.bases().length !== 0) {
        formData.append('primer2', primer2.bases());
      }
    } else {
      reverse = true;
      formData.append('primer', primer2!.bases());
      formData.append('nucleotideType', primer2!.nucleotideType());
    }
    formData.append('temperature', this.get('temperature')?.toString() ?? '37');
    const xhr = TaskRunner.runTask({
      url: '/1/api/oligo-secondary-structure',
      data: formData,
      dataType: 'json',
      processData: false,
      contentType: false,
      type: 'PUT',
    });
    return xhr
      .fail((res) => {
        notifications.notifyError(res);
      })
      .done((res) => {
        if (reverse) {
          res = {monomer2: res.monomer, homodimer2: res.homodimer};
        }
        return this.set({secondary: res});
      });
  }

  static createFromSelection(sequence: ComponentSequence, strand: number = 1): EditPrimerTabState {
    const selection = sequence.get('selection').assertNotEmpty();
    let bases = sequence.residueSlice(selection.start(), selection.end());
    if (strand === -1) {
      bases = revcomp(bases, sequence.get('_sequence').baseTable());
    }
    const primer = new BioJSSequencePrimer({bases, strand, nucleotideType: sequence.nucleotideType()});
    primer.setBindPosition(
      sequence.get('_sequence'),
      strand === 1 ? selection.end() - 1 : selection.start(),
      0
    );
    return new EditPrimerTabState({
      primer,
      sequence,
    });
  }

  static edit(
    primer: BioJSSequencePrimer,
    sequence: ComponentSequence,
    analyzeSecondary: boolean = false
  ): void {
    const existing = sequence
      .fileState()
      .buffers()
      .getTab((tab) => {
        if (primer.id != null && tab instanceof EditPrimerTabState) {
          return tab.get('primer').id === primer.id || tab.get('primer2')?.id === primer.id;
        } else {
          return false;
        }
      });
    if (existing != null) {
      existing.set({
        active: true,
        analyzeSecondary,
      });
    } else {
      const tab = new EditPrimerTabState({
        primer,
        sequence,
        analyzeSecondary,
      });
      sequence.fileState().buffers().addTab(tab);
    }
  }

  static editPair(
    fwdPrimer: BioJSSequencePrimer,
    revPrimer: BioJSSequencePrimer,
    sequence: ComponentSequence,
    analyzeSecondary: boolean = false
  ): void {
    const existing = sequence
      .fileState()
      .buffers()
      .getTab((tab) => {
        if (fwdPrimer.id != null && revPrimer.id != null && tab instanceof EditPrimerTabState) {
          return tab.get('primer').id === fwdPrimer.id && tab.get('primer2')?.id === revPrimer.id;
        } else {
          return false;
        }
      });
    if (existing != null) {
      existing.set({
        active: true,
        analyzeSecondary,
      });
    } else {
      const tab = new EditPrimerTabState({
        primer: fwdPrimer,
        primer2: revPrimer,
        sequence,
        analyzeSecondary,
      });
      sequence.fileState().buffers().addTab(tab);
    }
  }

  removePrimers(primer: BioJSSequencePrimer | null, primer2: BioJSSequencePrimer | null): void {
    [primer, primer2].forEach((p) => {
      if (p?.isNew()) {
        this._sequence.sequencePrimers().remove(p);
      }
    });
  }

  addValidPrimers(): void {
    // We shouldn't add invalid primers to a sequence.
    if (!this._sequence.isEmpty()) {
      [this.get('primer'), this.get('primer2')].forEach(
        (primer) => primer && this._sequence.sequencePrimers().add(primer)
      );
    }
  }

  close(reset: boolean = true): void {
    if (reset) {
      [
        {primer: this.get('primer'), original: this.originalPrimer},
        {primer: this.get('primer2'), original: this.originalPrimer2},
      ].forEach(({primer, original}) => {
        if (primer?.has('id')) {
          if (AssemblyPrimers.isAssemblyPrimer(this._sequence, primer)) {
            primer.set({name: original?.name}); // other attributes are automatically saved
          } else {
            primer.set(original as SequencePrimerAttributes);
          }
        }
      });
    }
    this.stopListening(this._sequence);
    this.stopListening(this.get('primer'));
    if (this.get('primer2') != null) {
      this.stopListening(this.get('primer2'));
    }
    this.removePrimers(this.get('primer'), this.get('primer2'));
    super.close();
  }

  isSaving(index: number): boolean {
    return this.get('saving')[index];
  }

  saving(index: number, value: boolean): void {
    const savingCopy = [...this.get('saving')] as [boolean, boolean];
    savingCopy[index] = value;
    this.set({saving: savingCopy});
  }

  defaults(): EditPrimerTabStateDefaults {
    return {
      ...super.defaults(),
      title: 'Design Primer',
      primer: new BioJSSequencePrimer(),
      primer2: null,
      saving: [false, false],
      checking: false,
    };
  }
}

export default EditPrimerTabState;
